import React, { FC, useEffect, useState } from "react";
import DashboardHeader from "@/components/DashboardHeader";
import { useAppSelector } from "@/hooks/hook";
import { BillingList } from "./BillingList";
import { GET, POST } from "@/services/api";
import { API_ROUTES } from "@/services/environment";
import Alert from "@/components/alert/Alert";
import { useDispatch } from "react-redux";
import { updateCardInfo } from "@/redux/slices/userSlice";
import FormModal from "@/components/modals/FormModal";
import { loadStripe } from "@stripe/stripe-js";
import visaCardImg from "@/assets/icons/bankIcon/visa.png";
import americanExpImg from "@/assets/icons/bankIcon/americanex.png";
import masterCardImg from "@/assets/icons/bankIcon/master.png";
import unionPayImg from "@/assets/icons/bankIcon/unionpay.png";
import discoverImg from "@/assets/icons/bankIcon/discover.png";
import dinnerClubImg from "@/assets/icons/bankIcon/dinnerClub.png";
import jdcImg from "@/assets/icons/bankIcon/jcb.png";

import { Elements } from "@stripe/react-stripe-js";
import { StripeCardModal } from "./StripeCardModal";
import { format } from "date-fns";
import ChangesAlert from "@/components/alert/ChangesAlert";
import {
    activateSubscription,
    cancelSubscription,
    updateBillingInfo,
    updateCardDetails,
} from "@/redux/slices/billingInfoSlice";
import useLoading from "@/hooks/useLoading";
import Lottie from "lottie-react";
import { Button, convertStripeTime } from "@thdinc/hpf-shared";
import { Params } from "react-router";
const stripePK = process.env.REACT_APP_STRIPE_PK || "";
const stripePromise = loadStripe(stripePK);

interface CardInfo {
    card_number: string;
    card_brand: string;
    card_expiry_date: string;
    card_status: boolean;
}
interface BillingParams {
    params?: Readonly<Params<string>>;
}
const BillingPage: FC<BillingParams> = ({ params }) => {

    const {
        setUpIntent,
        billingDetailsApi,
        savePaymentMethod,
        pendingInvoicesApi,
        cancelSubscriptionApi,
        subscriptApi,
        billingHistoryApi
    } = API_ROUTES;
    const dispatch = useDispatch();
    const user = useAppSelector((store) => store.user);
    const businessId = useAppSelector((store) => store.business);
    const billing = useAppSelector((store) => store.billing);

    const { startLoading, stopLoading, isLoading: useLoad } = useLoading();
    const { isLoading: isPastLoading, startLoading: startPastLoading, stopLoading: stopPastLoading } = useLoading();
    const [isOpen, setIsOpen] = useState(false);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [addCardVisibility, setAddCardVisiblity] = useState<boolean>(false);
    const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
    const [confirmationModalBtnText, setConfirmationModalBtnText] = useState<string>('');
    const [pendingInvoice, setPendingInvoice] = useState<any | null>(null);
    const [history, setHistory] = useState<
        | {
            name: string;
            amount: number;
            status: 'paid' | 'open';
            create_at: string;
            stripe_time: number;
        }[]
        | null
    >(null);

    useEffect(() => {
        if (user.refreshToken) {
            user.roles !== "admin" && setIsOpen(!user.card);
            fetchClientSecret();
            fetchBillingDetail();
            fetchPendingInvoice();
            getBillingHistory()
        }
    }, [user, businessId.currentBusiness]);

    // Get client secret to add stripe card
    const fetchClientSecret = async () => {
        try {
            const res = await POST(
                setUpIntent,
                {
                    customerId: user.customerId,
                },
                user.refreshToken
            );
            setClientSecret(res.data.clientSecret);
        } catch (error) {
            console.log("error", error);
        }
    };
    // get pending Invoices
    const fetchPendingInvoice = async () => {
        if (businessId.currentBusiness) {
            try {
                const res = await GET(
                    `${pendingInvoicesApi}/${businessId.currentBusiness}`,
                    user.refreshToken
                );
                if (res.data) {
                    setPendingInvoice(res.data);
                }
            } catch (error) {
                console.log(error);
            }
        }
    };
    const updateCard = (info: CardInfo) => {
        dispatch(
            updateCardDetails({
                card_brand: info.card_brand,
                card_expiry_date: info.card_expiry_date,
                card_number: info.card_number,
            })
        );
    };
    const fetchBillingDetail = async () => {
        if (businessId.currentBusiness) {
            try {
                const response = await GET(
                    `${billingDetailsApi}/${businessId.currentBusiness}`,
                    user.refreshToken
                );
                dispatch(updateBillingInfo(response.data[0]));
            } catch (error) {
                console.log(error);
            }
        }

    };
    const getBillingHistory = async () => {
        if (businessId.currentBusiness) {
            try {
                startPastLoading();
                const res = await GET(
                    `${billingHistoryApi}/${businessId.currentBusiness}`,
                    user.refreshToken
                );
                setHistory(res.data);
            } catch (error) {
                console.log(error);
            } finally {
                stopPastLoading();
            }
        }
    };
    const closeModal = () => {
        if (addCardVisibility) {
            dispatch(updateCardInfo({ cardStatus: true, application_access: true }));
            setAddCardVisiblity(false)
        }
        setIsOpen(false);
    };
    // Save stripe card info
    const handleSave = async (
        paymentMethodId: string,
        productId?: string,
        priceId?: string
    ) => {
        try {
            setIsLoading(true);
            const res = await POST(
                savePaymentMethod,
                {
                    customerId: user.customerId,
                    paymentMethodId,
                    productId,
                    priceId,
                },
                user.refreshToken
            );
            if (res.data.success) {
                fetchBillingDetail();
                setIsOpen(false);
                if (!user?.card) {
                    dispatch(updateCardInfo({ cardStatus: true, application_access: true }));
                    Alert({ message: "Card added successfully! Your 14-day free trial has started." });
                } else {
                    dispatch(updateCardInfo({ cardStatus: true }));
                    Alert({ message: "Payment method saved successfully" });
                }
                fetchClientSecret()
                updateCard(res.data);
                getBillingHistory()
                fetchClientSecret()
            } else if (!res.data.success) {
                Alert({ type: "error", message: res.data.message });
            }
        } catch (error) {
            console.log("error", error);
        } finally {
            setIsLoading(false);
        }
    };
    const subscribeFun = async () => {
        try {
            startLoading();
            const res = await POST(
                `${subscriptApi}/${user.customerId}`,
                {},
                user.refreshToken
            );
            if (!res.data.success) {
                Alert({ type: "error", message: res.data.message });
            } else if (res.data.success) {
                dispatch(activateSubscription());
                getBillingHistory()
                Alert({ message: res.data.message });
            }
        } catch (error) {
            console.log(error);
        } finally {
            stopLoading();
            setConfirmationModal(false);
        }
    };
    const cancelSub = async () => {
        try {
            startLoading();
            const res = await POST(
                `${cancelSubscriptionApi}/${user.customerId}`,
                {},
                user.refreshToken
            );
            if (res?.data.success) {
                dispatch(cancelSubscription());
                getBillingHistory()
                Alert({ message: res?.data.message });
            } else if (!res?.data.success) {
                Alert({ type: "error", message: res.data.message });
            }
        } catch (error) {
            console.log(error);
        } finally {
            stopLoading();
            setConfirmationModal(false);
        }
    };
    // conditions
    // if user cancelled subscription and the subscription status is active for this month
    const showSubscribe =
        billing.billingDetails?.cancel_at_period_end &&
        billing.billingDetails?.subscription_status === "active";
    // If subscribtion is cancelled and subscription status is active (for ongoing month)
    const showUnsubscribe =
        !billing?.billingDetails?.cancel_at_period_end &&
        (billing.billingDetails?.subscription_status === "active"
            || billing.billingDetails?.subscription_status === "trialing");
    // if subscription is not cancelled but the month end and payment is due
    const activeAndDue =
        !billing?.billingDetails?.cancel_at_period_end &&
        billing.billingDetails?.subscription_status === "past_due";
    // if subscription is cancelled and month has ended
    const subCancelled =
        billing.billingDetails?.subscription_status === "canceled";

    let remainingAmount = 0
    pendingInvoice?.map((_: any) => {
        remainingAmount += _.amount_remaining
    })
    return (
        <div>
            <FormModal
                overflow="overflow-hidden "
                modalTitle={addCardVisibility ? 'Add New Card' : user.card ? "Update Card" : "Add Card"}
                isOpen={isOpen}
                width="lg:w-3/6 md:w-5/6 w-full"
                height="overflow-y-auto"
                onClose={closeModal}>
                {addCardVisibility ?
                    <div>
                        <Lottie
                            style={{ height: '233px', width: '100%' }}
                            animationData={require('../../assets/lottie/cardAnimation.json')} loop={false} />
                        <p className="text-center text-[#FF5100] mb-12">Card Added Successfully</p>
                    </div>
                    : <Elements stripe={stripePromise}>
                        {clientSecret && businessId.currentBusiness && (
                            <StripeCardModal
                                clientSecret={clientSecret}
                                businessId={businessId.currentBusiness}
                                onSave={handleSave}
                                isLoading={isLoading}
                                isCard={user.card}
                            />
                        )}
                    </Elements>}
            </FormModal>
            <div className="flex items-start md:items-center gap-1 flex-wrap justify-between mt-2 sticky top-[64px] bg-p-gray z-30">
                <div className="flex gap-3 items-center cursor-pointer w-full">
                    <DashboardHeader width="w-full" title="Billing">
                        <div className="flex items-center gap-3 flex-wrap md:flex-nowrap mt-2 md:mt-0 "></div>
                    </DashboardHeader>
                </div>
                <div className="flex justify-between gap-5 w-full md:flex-nowrap flex-wrap">
                    {/* Billing Card */}
                    <div className="bg-white w-full p-4 rounded-2xl">
                        <div className="flex justify-between items-center">
                            <p className="font-semibold text-xl">Upcoming Billing</p>
                            {user.roles !== 'admin' && <div className="flex gap-2 items-center">
                                {showSubscribe && (
                                    <Button
                                        onClick={() => {
                                            setConfirmationModalBtnText('Subscribe');
                                            setConfirmationModal(true)
                                        }}
                                        className={`bg-primary-red px-6 py-1 rounded-full text-white`}
                                    >
                                        {'Subscribe'}

                                    </Button>
                                )}
                                {showUnsubscribe && (
                                    <Button
                                        onClick={() => {
                                            setConfirmationModalBtnText('Cancel Subscription');
                                            setConfirmationModal(true)
                                        }}
                                        className="bg-gray-400 px-6 py-1 rounded-full text-white"
                                    >
                                        {'Cancel Subscription'}
                                    </Button>
                                )}
                                {subCancelled && (
                                    <Button
                                        onClick={() => {
                                            setConfirmationModalBtnText('Subscribe and Pay');
                                            setConfirmationModal(true)
                                        }}
                                        className={`bg-primary-red px-6 py-1 rounded-full text-white`}
                                    >
                                        {'Subscribe and Pay'}

                                    </Button>
                                )}
                                {activeAndDue && (
                                    <Button
                                        onClick={() => {
                                            setConfirmationModalBtnText('Pay');
                                            setConfirmationModal(true)
                                        }}
                                        className={`bg-primary-red px-6 py-1 rounded-full text-white`}
                                    >
                                        {'Pay'}
                                    </Button>
                                )}
                            </div>}
                        </div>
                        {pendingInvoice?.length > 0 && (
                            <div className="text-sm text-primary-red font-semibold">
                                <p className="text-sm text-primary-red font-semibold">
                                    Pending Invoices: {pendingInvoice?.length}
                                </p>
                                <p>Pending Amount: ${remainingAmount / 100}</p>
                            </div>
                        )}
                        <div className="flex justify-between  mt-8">
                            <div>
                                <p className="font-semibold">Name</p>
                                <p className="text-gray-500">
                                    {/* {user.first_name + " " + user?.last_name} */}
                                    {businessId?.currentBusinessOwnerName}
                                </p>
                            </div>
                            <div>
                                <p className="font-semibold">Date</p>
                                <p className="text-gray-500">
                                    {/* {convertStripeTime(parseInt(upcomingBilling?.next_billing_date as string))} */}
                                    {billing?.billingDetails?.next_billing_date &&
                                        format(
                                            convertStripeTime(
                                                parseInt(billing?.billingDetails?.next_billing_date)
                                            ),
                                            " MM-dd-yyyy"
                                        )}
                                </p>
                            </div>
                            <div>
                                <p className="font-semibold">Amount</p>
                                <p className="text-gray-500">
                                    $
                                    {billing?.billingDetails?.upcoming_amount &&
                                        billing?.billingDetails?.upcoming_amount / 100}
                                </p>
                            </div>
                        </div>
                    </div>
                    {/* Card detail */}
                    <div className="bg-white w-full p-4 rounded-2xl">
                        <div className="flex justify-between items-center">
                            <p className="font-semibold text-lg">Card Details</p>
                            {user.roles !== 'admin' && <Button
                                className=" px-6 py-2 rounded-full bg-gray-200 text-sm font-semibold "
                                onClick={() => setIsOpen(true)}
                            >
                                {user.card ? "Update Card" : "Add Card"}
                            </Button>}
                        </div>
                        <div className="  mt-8">
                            <div className="flex justify-between items-start">
                                <div className="">
                                    <p className="text-2xl tracking-wider font-semibold">
                                        {" "}
                                        <span className="font-extrabold"> **** **** ****</span>{" "}
                                        {billing?.billingDetails?.card_number}
                                    </p>
                                    {user.roles !== 'admin' && <div className="flex items-center justify-between text-primary-red font-semibold">
                                        <p>{billing?.billingDetails?.card_expiry_date}</p>
                                    </div>}
                                </div>
                                {billing?.billingDetails?.card_brand === "visa" && (
                                    <img src={visaCardImg} width={40} height={40} alt="card" />
                                )}
                                {billing?.billingDetails?.card_brand === "american_express" && (
                                    <img src={americanExpImg} width={40} height={40} alt="card" />
                                )}
                                {billing?.billingDetails?.card_brand === "mastercard" && (
                                    <img src={masterCardImg} width={40} height={40} alt="card" />
                                )}
                                {billing?.billingDetails?.card_brand === "union_pay" && (
                                    <img src={unionPayImg} width={40} height={40} alt="card" />
                                )}
                                {billing?.billingDetails?.card_brand === "discover" && (
                                    <img src={discoverImg} width={40} height={40} alt="card" />
                                )}
                                {billing?.billingDetails?.card_brand === "diners_club" && (
                                    <img src={dinnerClubImg} width={40} height={40} alt="card" />
                                )}
                                {billing?.billingDetails?.card_brand === "jcb" && (
                                    <img src={jdcImg} width={40} height={40} alt="card" />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <BillingList
                    getBillingHistory={getBillingHistory}
                    history={history}
                    isLoading={isPastLoading}
                />
            </div>
            {activeAndDue || showSubscribe || subCancelled ? (
                <ChangesAlert
                    title={"Save Changes"}
                    desc={`Are you sure you want to subscribe ?`}
                    positiveButtonText={confirmationModalBtnText ?? "Subscribe"}
                    negativeButtonText={"Cancel"}
                    isLoading={useLoad}
                    handleDiscardChanges={() => {
                        setConfirmationModal(false);
                    }}
                    handleSaveChanges={subscribeFun}
                    isOpen={confirmationModal}
                />
            ) : (
                <ChangesAlert
                    title={"Save Changes"}
                    desc={`Are you sure you want to unsubscribe ?`}
                    positiveButtonText={confirmationModalBtnText ?? "Unsubscribe"}
                    negativeButtonText={"Cancel"}
                    isLoading={useLoad}
                    handleDiscardChanges={() => {
                        setConfirmationModal(false);
                    }}
                    handleSaveChanges={cancelSub}
                    isOpen={confirmationModal}
                />
            )}
        </div>
    );
};

export default BillingPage;
